import React, { useRef, useState, useEffect, useCallback } from 'react';

import styled from 'styled-components';

// direction: 'horizontal' | 'vertical'
// thumbSize: thumb 길이 (px, 기본 100). 'auto'를 넘기면 콘텐츠/뷰포트 비율 기반 동적 계산 (최소 minThumbSize)
// minThumbSize: thumbSize='auto' 일 때 최소 길이 (px, 기본 20)
// thumbColor: thumb 색상 (기본 theme.subKey)
// trackColor: track 색상 (기본 theme.buttonFill)
// scrollRef: 스크롤 대상 요소의 ref
// showTrackAlways: 스크롤 불가능해도 트랙 표시 여부 (기본 true)
// thumbThickness: thumb 두께(px, 기본 5)
// trackOffset: 콘텐츠와 트랙 사이 간격(px). vertical은 margin-left, horizontal은 margin-top에 적용 (기본 10)
export default function CustomScrollbar(props) {
  const {
    direction = 'horizontal',
    thumbSize = 100,
    minThumbSize = 10,
    thumbColor,
    trackColor,
    scrollRef,
    showTrackAlways = true,
    isColorScrollBar = false,
    isColorLine = false,
    isTrackThin = false,
    thumbThickness = 5,
    trackOffset = 10,
  } = props;

  const isHorizontal = direction === 'horizontal';
  const isAutoThumb = thumbSize === 'auto';

  const [isScrollable, setIsScrollable] = useState(false);
  const [autoThumbSize, setAutoThumbSize] = useState(minThumbSize);

  const effectiveThumbSize = isAutoThumb ? autoThumbSize : thumbSize;

  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef(0);
  const dragStartScroll = useRef(0);

  // auto 모드 — 콘텐츠/뷰포트 비율로 thumb 길이 계산 //
  const updateAutoThumbSize = useCallback(() => {
    if (!isAutoThumb) return;

    const el = scrollRef?.current;
    const track = trackRef.current;

    if (!el || !track) return;

    const totalSize = isHorizontal ? el.scrollWidth : el.scrollHeight;
    const viewSize = isHorizontal ? el.clientWidth : el.clientHeight;
    const trackSize = isHorizontal ? track.clientWidth : track.clientHeight;

    if (totalSize === 0 || trackSize === 0) return;

    const ratio = viewSize / totalSize;
    const computed = Math.max(minThumbSize, Math.floor(ratio * trackSize));

    setAutoThumbSize((prev) => prev !== computed ? computed : prev);
  }, [isAutoThumb, isHorizontal, scrollRef, minThumbSize]);

  // thumb DOM 직접 위치 업데이트 (리렌더 없이) //
  const updateThumbDirect = useCallback((position) => {
    const thumb = thumbRef.current;

    if (!thumb) return;

    const prop = isHorizontal ? 'left' : 'top';
    thumb.style[prop] = position + 'px';
  }, [isHorizontal]);

  // 스크롤 thumb 위치 계산 //
  const updateScrollbar = useCallback(() => {
    const el = scrollRef?.current;

    if (!el) return;

    const totalSize = isHorizontal ? el.scrollWidth : el.scrollHeight;
    const viewSize = isHorizontal ? el.clientWidth : el.clientHeight;
    const scrollPos = isHorizontal ? el.scrollLeft : el.scrollTop;
    const canScroll = totalSize > viewSize;

    setIsScrollable(canScroll);

    if (canScroll) {
      const scrollRatio = scrollPos / (totalSize - viewSize);
      const trackSize = isHorizontal
        ? (trackRef.current?.clientWidth || viewSize)
        : (trackRef.current?.clientHeight || viewSize);

      const newPosition = scrollRatio * (trackSize - effectiveThumbSize);

      updateThumbDirect(newPosition);
    }
  }, [scrollRef, isHorizontal, effectiveThumbSize, updateThumbDirect]);

  // 스크롤 이벤트 리스너 등록 //
  useEffect(() => {
    const timer = setTimeout(() => {
      updateAutoThumbSize();
      updateScrollbar();
    }, 100);

    const el = scrollRef?.current;

    if (!el) return () => clearTimeout(timer);

    const onScrollOrResize = () => {
      updateAutoThumbSize();
      updateScrollbar();
    };

    el.addEventListener('scroll', updateScrollbar);
    window.addEventListener('resize', onScrollOrResize);

    // 콘텐츠 크기 변화 감지 //
    const resizeObserver = new ResizeObserver(onScrollOrResize);
    resizeObserver.observe(el);
    if (el.firstElementChild) resizeObserver.observe(el.firstElementChild);

    return () => {
      clearTimeout(timer);
      el.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', onScrollOrResize);
      resizeObserver.disconnect();
    };
  }, [scrollRef, updateScrollbar, updateAutoThumbSize]);

  // thumb 마운트 시 초기 위치 동기화 //
  useEffect(() => {
    if (isScrollable) updateScrollbar();
  }, [isScrollable, updateScrollbar]);

  // thumb 드래그로 스크롤 조작 //
  const onThumbMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStart.current = isHorizontal ? e.clientX : e.clientY;

    const el = scrollRef?.current;

    dragStartScroll.current = isHorizontal ? (el?.scrollLeft || 0) : (el?.scrollTop || 0);

    const onMouseMove = (e) => {
      if (!isDragging.current || !scrollRef?.current || !trackRef.current) return;

      const el = scrollRef.current;
      const trackSize = isHorizontal ? trackRef.current.clientWidth : trackRef.current.clientHeight;
      const totalSize = isHorizontal ? el.scrollWidth : el.scrollHeight;
      const viewSize = isHorizontal ? el.clientWidth : el.clientHeight;
      const maxScroll = totalSize - viewSize;
      const maxThumbPos = trackSize - effectiveThumbSize;

      const delta = (isHorizontal ? e.clientX : e.clientY) - dragStart.current;
      const scrollDelta = (delta / maxThumbPos) * maxScroll;

      if (isHorizontal) el.scrollLeft = dragStartScroll.current + scrollDelta;
      else el.scrollTop = dragStartScroll.current + scrollDelta;
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [scrollRef, isHorizontal, effectiveThumbSize]);

  if (!showTrackAlways && !isScrollable) return null;

  return (
    <Track
      ref={trackRef}
      $direction={direction}
      $trackColor={trackColor}
      $isTrackThin={isTrackThin}
      $trackOffset={trackOffset}>
      {
        isScrollable &&
        <Thumb
          ref={thumbRef}
          $direction={direction}
          $thumbSize={effectiveThumbSize}
          $thumbColor={thumbColor}
          $isColorScrollBar={isColorScrollBar}
          $isColorLine={isColorLine}
          $isTrackThin={isTrackThin}
          $thumbThickness={thumbThickness}
          onMouseDown={onThumbMouseDown} />
      }
    </Track>
  );
}

const Track = styled.div`
  position: relative;

  ${props => props.$direction === 'horizontal' ? `
    width: 100%;
    height: ${props.$isTrackThin ? '1px' : '5px'};
    margin-top: ${props.$trackOffset}px;
  ` : `
    width: ${props.$isTrackThin ? '1px' : '5px'};
    height: 100%;
    margin-left: ${props.$trackOffset}px;
  `}

  border-radius: 300px;
  background-color: ${props => {
    if (props.$trackColor) return props.$trackColor;
    return props.$isTrackThin ? props.theme.line : props.theme.buttonFill;
  }};

  align-self: ${props => props.$isTrackThin ? 'flex-end' : 'auto'};
  flex-shrink: 0;
`;

const Thumb = styled.div`
  position: absolute;

  ${props => props.$direction === 'horizontal' ? `
    top: ${(props.$isTrackThin || props.$thumbThickness !== 5) ? '50%' : '0'};
    transform: ${(props.$isTrackThin || props.$thumbThickness !== 5) ? 'translateY(-50%)' : 'none'};
    left: 0;
    width: ${props.$thumbSize}px;
    height: ${props.$thumbThickness}px;
  ` : `
    left: ${(props.$isTrackThin || props.$thumbThickness !== 5) ? '50%' : '0'};
    transform: ${(props.$isTrackThin || props.$thumbThickness !== 5) ? 'translateX(-50%)' : 'none'};
    top: 0;
    width: ${props.$thumbThickness}px;
    height: ${props.$thumbSize}px;
  `}

  border-radius: 50px;
  background-color: ${props => props.$thumbColor || (props.$isColorLine ? props.theme.line : (props.$isColorScrollBar ? props.theme.scrollBar : props.theme.subKey))};
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;
